import React from 'react';
import { Heart, Users, Globe, HandHeart, Home, Utensils, ExternalLink } from 'lucide-react';

const OutreachPage = () => {
  const blanketProjectImpact = [
    {
      number: '500+',
      label: 'Blankets Planned for Distribution',
      icon: Heart
    },
    {
      number: '12',
      label: 'Community Partnerships',
      icon: Users
    },
    {
      number: '3',
      label: 'Service Areas',
      icon: Globe
    }
  ];

  const services = [
    {
      title: 'Blanket Distribution',
      description: 'Providing warm blankets to those facing harsh weather conditions, ensuring comfort and dignity for our community members in need.',
      icon: Home,
      image: 'https://images.unsplash.com/photo-1488521787991-ed7bbaae773c?q=80&w=400&h=250&auto=format&fit=crop'
    },
    {
      title: 'Food Outreach',
      description: 'Distributing nutritious meals and food supplies to individuals and families experiencing food insecurity in the Dallas metro area.',
      icon: Utensils,
      image: 'https://images.unsplash.com/photo-1593113598332-cd288d649433?q=80&w=400&h=250&auto=format&fit=crop'
    },
    {
      title: 'Sanitary Supplies',
      description: 'Essential hygiene products and sanitary supplies to maintain health and dignity for underserved community members.',
      icon: HandHeart,
      image: 'https://images.unsplash.com/photo-1559027615-cd4628902d4a?q=80&w=400&h=250&auto=format&fit=crop'
    }
  ];

  return (
    <div className="pt-16 lg:pt-20 pb-20 lg:pb-0">
      {/* Hero Section */}
      <section className="py-12 lg:py-20 bg-gradient-to-br from-orange-50 to-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-8 lg:mb-12">
            <h1 className="text-4xl md:text-5xl lg:text-6xl font-bold text-gray-800 mb-6 font-display">
              Community Outreach
            </h1>
            <p className="text-lg lg:text-xl text-gray-600 max-w-4xl mx-auto font-sans">
              Following Christ's example of love in action through The Blanket Project - 
              our ministry serving underprivileged and underserved communities in the Dallas metro area.
            </p>
          </div>
        </div>
      </section>

      {/* The Blanket Project Hero */}
      <section className="py-16 bg-gradient-to-br from-[#f17b21] to-orange-600">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto text-center text-white">
            <h2 className="text-3xl md:text-4xl lg:text-5xl font-bold mb-6 font-display">
              The Blanket Project
            </h2>
            <p className="text-xl lg:text-2xl mb-8 font-light leading-relaxed">
              "Covering the Needy with God's Love"
            </p>
            <div className="bg-white/10 backdrop-blur-sm rounded-2xl p-6 mb-8">
              <p className="text-lg italic mb-4">
                "For I was hungry and you gave me something to eat, I was thirsty and you gave me something to drink, 
                I was a stranger and you invited me in, I needed clothes and you clothed me..."
              </p>
              <p className="font-semibold">— Matthew 25:35-36</p>
            </div>
            <div className="flex flex-col sm:flex-row gap-4 justify-center">
              <a 
                href="https://theblanket.us"
                target="_blank"
                rel="noopener noreferrer"
                className="bg-white text-[#f17b21] hover:bg-gray-100 px-8 py-4 rounded-lg font-semibold transition-colors duration-300 inline-flex items-center justify-center"
              >
                Visit The Blanket Project <ExternalLink className="w-5 h-5 ml-2" />
              </a>
              <a 
                href="https://theblanket.us/volunteer"
                target="_blank"
                rel="noopener noreferrer"
                className="border-2 border-white text-white hover:bg-white hover:text-[#f17b21] px-8 py-4 rounded-lg font-semibold transition-colors duration-300"
              >
                Volunteer With Us
              </a>
            </div>
          </div>
        </div>
      </section>

      {/* Impact Stats */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 font-display">
              Our Growing Impact
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-sans">
              Preparing to serve the Dallas metro area with love and dedication
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-4xl mx-auto">
            {blanketProjectImpact.map((stat, index) => {
              const IconComponent = stat.icon;
              return (
                <div key={index} className="text-center">
                  <div className="w-20 h-20 bg-[#f17b21] rounded-full flex items-center justify-center mx-auto mb-4">
                    <IconComponent className="w-10 h-10 text-white" />
                  </div>
                  <div className="text-4xl font-bold text-[#f17b21] mb-2 font-display">{stat.number}</div>
                  <p className="text-gray-600 font-sans">{stat.label}</p>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Services We Provide */}
      <section className="py-16 bg-gray-50">
        <div className="container mx-auto px-4">
          <div className="text-center mb-12">
            <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 font-display">
              How We Serve Our Community
            </h2>
            <p className="text-lg text-gray-600 max-w-3xl mx-auto font-sans">
              Through Christ's love, we provide essential resources and support to those who need it most
            </p>
          </div>

          <div className="grid grid-cols-1 md:grid-cols-3 gap-8 max-w-6xl mx-auto">
            {services.map((service, index) => {
              const IconComponent = service.icon;
              return (
                <div key={index} className="bg-white rounded-xl shadow-lg overflow-hidden hover:shadow-xl transition-shadow duration-300">
                  <div className="h-48 overflow-hidden">
                    <img 
                      src={service.image}
                      alt={service.title}
                      className="w-full h-full object-cover hover:scale-105 transition-transform duration-300"
                    />
                  </div>
                  <div className="p-6">
                    <div className="flex items-center space-x-3 mb-4">
                      <div className="w-12 h-12 bg-[#f17b21] rounded-full flex items-center justify-center">
                        <IconComponent className="w-6 h-6 text-white" />
                      </div>
                      <h3 className="text-xl font-bold text-gray-800 font-display">{service.title}</h3>
                    </div>
                    <p className="text-gray-600 leading-relaxed font-sans">{service.description}</p>
                  </div>
                </div>
              );
            })}
          </div>
        </div>
      </section>

      {/* Get Involved Section */}
      <section className="py-16 bg-white">
        <div className="container mx-auto px-4">
          <div className="max-w-4xl mx-auto">
            <div className="bg-gradient-to-br from-orange-50 to-white rounded-2xl p-8 lg:p-12 text-center">
              <h2 className="text-3xl md:text-4xl font-bold text-gray-800 mb-6 font-display">
                Join Our Journey of Compassion
              </h2>
              <p className="text-lg text-gray-600 mb-8 font-sans">
                Whether through volunteering, donating, or spreading the word, you can help us bring warmth and hope to those who need it most.
              </p>
              <div className="flex flex-col sm:flex-row gap-4 justify-center">
                <a 
                  href="https://theblanket.us/volunteer"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="bg-[#f17b21] hover:bg-[#d86919] text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-300 inline-flex items-center justify-center"
                >
                  Become a Volunteer
                </a>
                <a 
                  href="https://theblanket.us/contact"
                  target="_blank"
                  rel="noopener noreferrer"
                  className="border-2 border-[#f17b21] text-[#f17b21] hover:bg-[#f17b21] hover:text-white px-8 py-4 rounded-lg font-semibold transition-colors duration-300"
                >
                  Get In Touch
                </a>
              </div>
            </div>
          </div>
        </div>
      </section>
    </div>
  );
};

export default OutreachPage;
